import { FormikHandlers, FormikHelpers, FormikErrors, FormikState } from 'formik';
import { Box, Flex } from '@chakra-ui/react';
import { colourGroups as clrGroups } from 'constants/theme';
import { Alert, AlertDescription, AlertIcon, FormColourPicker, SimpleGrid } from 'components/UI';
import { ISettingsValues } from 'types';
import ProductFieldTitle from 'components/common/ProductFieldTitle';
import { validateForm } from 'utils/helpers';
import SettingsTextContainer from 'components/Settings/SettingsForm/SettingsTextContainer';
import SettingsFormControl from 'components/Settings/SettingsForm/SettingsFormControl';
import { errorMessageNoColours } from 'constants/errors';

export type ColoursFormProps = {
  colourGroups?: typeof clrGroups;
  values: ISettingsValues | Record<string, never>;
  submitted: boolean;
  errors: FormikErrors<ISettingsValues | Record<string, never>>;
  onBlur: FormikHandlers['handleBlur'];
  onChange: FormikHandlers['handleChange'];
  touched: FormikState<ISettingsValues | Record<string, never>>['touched'];
  setFieldValue: FormikHelpers<ISettingsValues | Record<string, never>>['setFieldValue'];
};

const ColoursForm = ({ values, setFieldValue, onChange, colourGroups, ...props }: ColoursFormProps) => {
  if (!colourGroups)
    return (
      <Alert
        status="error"
        sx={{
          w: 'auto',
          mx: 12,
          mb: 12,
          mt: 8,
          borderRadius: 4,
        }}
        variant="solid"
      >
        <AlertIcon />
        <AlertDescription maxWidth="sm">{errorMessageNoColours}</AlertDescription>
      </Alert>
    );

  const formErrors = validateForm<ISettingsValues | Record<string, never>>(props);

  const renderColourGroups = Object.entries(colourGroups).map(([key, groupColours]) => {
    const renderColours = groupColours.map((colour) => (
      <SimpleGrid columns={2} mb={2} spacing={4} key={`${key}${colour}`} data-testid="colour-grid">
        <SettingsTextContainer>
          <ProductFieldTitle htmlFor={colour}>{colour} *</ProductFieldTitle>
        </SettingsTextContainer>
        <SettingsFormControl isInvalid={!!formErrors[colour]}>
          <FormColourPicker
            name={colour}
            value={values[colour]}
            setFieldValue={setFieldValue}
            inputProps={{
              bgColor: 'white',
              _hover: {
                bgColor: 'white',
              },
              _focus: {
                bgColor: 'white',
              },
            }}
            onChange={onChange}
            error={formErrors[colour]}
            containerProps={{ w: 'full' }}
          />
        </SettingsFormControl>
      </SimpleGrid>
    ));

    return [
      <Flex
        key={key}
        sx={{
          flexDir: 'column',
          justifyContent: 'flex-start',
          bgColor: 'base2Colour.500',
          alignItems: 'space-between',
          fontSize: 'sm',
          color: 'base2FontColour.500',
        }}
      >
        <Flex
          sx={{
            py: 2.5,
            justifyContent: 'flex-start',
            borderStyle: 'solid',
            borderBottomWidth: 2,
            mb: 4,
            borderColor: 'base2Colour.400',
            textTransform: 'uppercase',
          }}
        >
          {key}
        </Flex>
        {renderColours}
      </Flex>,
    ];
  }, []);

  return <Box>{renderColourGroups}</Box>;
};

export default ColoursForm;
